import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import PushButton from "@/components/PushButton";
import SecurityPostureCard from "@/components/SecurityPostureCard";
import { api } from "@/lib/api";
import { ArrowsClockwise, HardDrives, Warning, XCircle, Clock, CheckCircle, Pulse, ShieldWarning, Timer } from "@phosphor-icons/react";

const STAT_CARDS = [
  { key: "healthy", label: "Healthy", icon: CheckCircle, color: "text-emerald-400" },
  { key: "warning", label: "Warning", icon: Warning,     color: "text-amber-400" },
  { key: "failure", label: "Failure", icon: XCircle,     color: "text-red-400" },
  { key: "missed",  label: "Missed",  icon: Clock,       color: "text-slate-300" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [tv, setTv] = useState(null);
  const [mttr, setMttr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [a, b, c] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/tv-data"),
        api.get("/mttr/summary", { params: { days: 30 } }).catch(() => ({ data: null })),
      ]);
      setStats(a.data);
      setTv(b.data);
      setMttr(c.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Layout>
      <PageHeader
        title="Dashboard"
        subtitle="Backups + uptime monitors at a glance"
        actions={
          <>
            <PushButton />
            <Link to="/app/tv" data-testid="dashboard-tv-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/10 rounded-md transition-colors">
              <Pulse size={14} weight="bold" /> Open TV / NOC
            </Link>
            <button onClick={load} data-testid="dashboard-refresh-btn"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md transition-colors">
              <ArrowsClockwise size={14} weight="bold" /> Refresh
            </button>
          </>
        }
      />
      <div className="p-6 space-y-6" data-testid="dashboard-page">
        {/* Security posture — one-glance health of admin controls */}
        <SecurityPostureCard />

        {/* Backup stat cards */}
        <section>
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-3">Backups</h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard label="Total jobs" value={loading ? "—" : (stats?.total_jobs ?? 0)} icon={HardDrives} testid="stat-total-jobs" />
            {STAT_CARDS.map((s) => (
              <StatCard key={s.key} label={s.label} icon={s.icon} color={s.color}
                value={loading ? "—" : (stats?.counts?.[s.key] ?? 0)} testid={`stat-${s.key}`} />
            ))}
          </div>
        </section>

        {/* Monitor stat cards */}
        {tv && (
          <section>
            <h2 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-3">Uptime monitors</h2>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard label="Total monitors" value={tv.counts.monitors.total} icon={Pulse} testid="stat-total-monitors" />
              <StatCard label="Up"       value={tv.counts.monitors.up}       color="text-emerald-400" testid="stat-up" />
              <StatCard label="Degraded" value={tv.counts.monitors.degraded} color="text-amber-400"   testid="stat-degraded" />
              <StatCard label="Down"     value={tv.counts.monitors.down}     color="text-red-400"     testid="stat-down" />
              <StatCard label="Pending"  value={tv.counts.monitors.pending + tv.counts.monitors.paused} color="text-slate-300" testid="stat-pending" />
            </div>
          </section>
        )}

        {/* Incidents / MTTR */}
        {mttr && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">Incidents — last 30 days</h2>
              <Link to="/app/incidents" data-testid="dashboard-incidents-link"
                className="text-xs text-slate-300 hover:text-brand-cyan">View all →</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Open incidents" value={mttr.overall?.incidents_open ?? 0}
                icon={ShieldWarning} color="text-red-400" testid="stat-incidents-open" />
              <StatCard label="Closed (30d)"   value={mttr.overall?.incidents_closed ?? 0}
                icon={CheckCircle} color="text-emerald-400" testid="stat-incidents-closed" />
              <StatCard label="MTTR"
                value={mttr.overall?.mttr_seconds != null ? formatDur(mttr.overall.mttr_seconds) : "—"}
                icon={Timer} color="text-brand-cyan" testid="stat-mttr" />
              <StatCard label="Top affected client"
                value={topClient(mttr.per_client)}
                icon={Warning} color="text-amber-400" testid="stat-top-client" />
            </div>
          </section>
        )}

        {/* Recent failures */}
        <div className="sw-card">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Recent failures & warnings</h2>
            <Link to="/app/reports" data-testid="dashboard-view-all-reports"
              className="text-xs text-slate-300 hover:text-brand-cyan">View all →</Link>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500">loading…</div>
          ) : error ? (
            <div className="p-6 text-sm text-red-400">{error}</div>
          ) : (stats?.recent_failures?.length ?? 0) === 0 ? (
            <div className="p-10 text-center">
              <CheckCircle size={32} weight="duotone" className="text-emerald-400 mx-auto mb-2" />
              <div className="text-sm text-slate-200">All clear. No failures or warnings.</div>
              <div className="text-xs text-slate-500 mt-1">silence is the desired state</div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="recent-failures-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400 bg-background-deep/50">
                  <th className="text-left py-2.5 px-4">When</th>
                  <th className="text-left py-2.5 px-4">Status</th>
                  <th className="text-left py-2.5 px-4">Subject</th>
                  <th className="text-left py-2.5 px-4">Sender</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_failures.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-white/5">
                    <td className="py-2.5 px-4 text-xs text-slate-400 whitespace-nowrap">
                      {new Date(r.occurred_at).toLocaleString()}
                    </td>
                    <td className="py-2.5 px-4"><StatusBadge status={r.status} /></td>
                    <td className="py-2.5 px-4 text-slate-200 truncate max-w-md">{r.subject || "—"}</td>
                    <td className="py-2.5 px-4 text-xs text-slate-400 truncate max-w-xs">{r.sender || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Trend */}
        <div className="sw-card">
          <div className="px-4 py-3 border-b border-border">
            <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">7-day status trend</h2>
          </div>
          <div className="p-4 overflow-x-auto">
            {loading ? (
              <div className="text-xs text-slate-500">loading…</div>
            ) : (stats?.trend?.length ?? 0) === 0 ? (
              <div className="text-xs text-slate-500">no data yet — ingest your first backup report</div>
            ) : (
              <div className="flex items-end gap-2 h-32">
                {stats.trend.map((d) => {
                  const total = (d.healthy || 0) + (d.warning || 0) + (d.failure || 0);
                  const max = Math.max(...stats.trend.map(x => (x.healthy||0)+(x.warning||0)+(x.failure||0)), 1);
                  const h = Math.max(4, (total / max) * 120);
                  return (
                    <div key={d.date} className="flex-1 min-w-[40px] flex flex-col items-center gap-1.5" title={`${d.date}: ${total}`}>
                      <div className="w-full flex flex-col-reverse" style={{ height: `${h}px` }}>
                        {d.failure ? <div style={{ flex: d.failure }} className="bg-red-500/70" /> : null}
                        {d.warning ? <div style={{ flex: d.warning }} className="bg-amber-500/70" /> : null}
                        {d.healthy ? <div style={{ flex: d.healthy }} className="bg-emerald-500/70" /> : null}
                      </div>
                      <div className="text-[10px] text-slate-500 whitespace-nowrap">{d.date.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="sw-card p-4 sw-card-hover">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</span>
        {Icon && <Icon size={16} className={color || "text-slate-400"} weight="bold" />}
      </div>
      <div className={`font-display text-3xl font-bold mt-2 ${color || "text-white"}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function formatDur(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function topClient(perClient) {
  if (!perClient || !perClient.length) return "—";
  const ranked = [...perClient].sort((a, b) =>
    (b.incidents_open + b.incidents_closed) - (a.incidents_open + a.incidents_closed));
  const top = ranked[0];
  if (!top || (top.incidents_open + top.incidents_closed) === 0) return "all clear";
  const name = top.client_name || "—";
  return name.length > 14 ? name.slice(0, 13) + "…" : name;
}
